import log from '@/lib/logger';
import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Text } from "@/components/tailwind/text";
import { Description, Field, Label } from "@/components/tailwind/fieldset";
import { Input } from "@/components/tailwind/input";
import { Button } from "@/components/tailwind/button";
import { Pencil, AlertTriangle, Info, Car, Euro, Heart, Building2, PiggyBank } from "lucide-react";
import { useAuth } from "@/context/use-auth";
import { useOrdersTranslation } from "@/i18n/hooks";
import { getOrderCosts, updateOrder, OrderCostCalculation } from "@/services/order-services";
import { getSocialAccountEnabled } from "@/services/social-account-settings-services";
import { useToast } from "@/hooks/use-toast";
import { RadioGroup, RadioField, Radio } from "@/components/tailwind/radio";
import { OrderDetails } from "@/models/order";
import { UpdateOrderRequest } from "@/services/order-services";

interface OrderCostsSectionProps {
    order: OrderDetails;
    onOrderUpdate: (order: OrderDetails) => void;
    refreshTrigger?: number;
}

export function OrderCostsSection({ order, onOrderUpdate, refreshTrigger }: OrderCostsSectionProps) {
    const { t } = useOrdersTranslation();
    const { token } = useAuth();
    const { toast } = useToast();
    const [costs, setCosts] = useState<OrderCostCalculation | null>(null);
    const [editingField, setEditingField] = useState<"mileage" | "reimbursable" | "donation" | null>(null);
    const [mileageValue, setMileageValue] = useState(order.mileage?.toString() || "0");
    const [reimbursableValue, setReimbursableValue] = useState(order.reimbursableAmount?.toString() || "0.00");
    const [donationValue, setDonationValue] = useState(order.donationAmount?.toString() || "0.00");
    const [payWithPoints, setPayWithPoints] = useState(order.payWithPoints || false);
    const [paidFromSocialAccount, setPaidFromSocialAccount] = useState(order.paidFromSocialAccount || false);
    const [socialAccountEnabled, setSocialAccountEnabled] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        loadCosts();
    }, [order.id, order.items, order.payWithPoints, refreshTrigger]);

    useEffect(() => {
        setPayWithPoints(order.payWithPoints || false);
    }, [order.payWithPoints]);

    useEffect(() => {
        setPaidFromSocialAccount(order.paidFromSocialAccount || false);
    }, [order.paidFromSocialAccount]);

    useEffect(() => {
        async function loadSocialAccountEnabled() {
            if (!token) return;
            try {
                setSocialAccountEnabled(await getSocialAccountEnabled(token));
            } catch (_error) {
                setSocialAccountEnabled(false);
            }
        }
        loadSocialAccountEnabled();
    }, [token]);

    useEffect(() => {
        setDonationValue(order.donationAmount?.toString() || "0.00");
    }, [order.donationAmount]);

    async function loadCosts() {
        if (!token) return;

        try {
            const costData = await getOrderCosts(token, order.id);
            if (costData) {
                setCosts(costData);
                setPayWithPoints(costData.payWithPoints);
            }
        } catch (error) {
            log.error("Error loading costs:", error);
        }
    }

    async function handleSave(newPaymentMethod?: { payWithPoints: boolean; paidFromSocialAccount: boolean }) {
        if (!token) return;

        setIsLoading(true);

        const payWithPointsValue = newPaymentMethod?.payWithPoints ?? payWithPoints;
        const paidFromSocialValue = newPaymentMethod?.paidFromSocialAccount ?? paidFromSocialAccount;

        try {
        const updateData: Partial<UpdateOrderRequest> = {
            clientId: order.client.id,
            serviceId: order.service.id,
            helperId: order.helper?.id || null,
            submissionChannelId: order.submissionChannel?.id || "",
            comments: order.comments,
            payWithPoints: payWithPointsValue,
            paidFromSocialAccount: paidFromSocialValue
        };

            if (editingField === "mileage") {
                updateData.mileage = mileageValue === "" ? null : parseInt(mileageValue);
            }

            if (editingField === "reimbursable") {
                updateData.reimbursableAmount = reimbursableValue === "" ? null : parseFloat(reimbursableValue);
            }

            if (editingField === "donation") {
                updateData.donationAmount = donationValue === "" ? null : parseFloat(donationValue);
            }

            if (newPaymentMethod !== undefined) {
                updateData.mileage = order.mileage;
                updateData.reimbursableAmount = order.reimbursableAmount;
                updateData.donationAmount = order.donationAmount;
            }

            const updatedOrder = await updateOrder(token, order.id, updateData);

            if (updatedOrder) {
                onOrderUpdate(updatedOrder);
                await loadCosts();
                setEditingField(null);

                const wasPayWithPoints = order.payWithPoints || false;
                const isNowPayWithPoints = updatedOrder.payWithPoints || false;
                const oldMileage = order.mileage || 0;
                const newMileage = parseInt(mileageValue) || 0;
                const oldReimbursable = order.reimbursableAmount || 0;
                const newReimbursable = parseFloat(reimbursableValue) || 0;
                const oldDonation = order.donationAmount || 0;
                const newDonation = parseFloat(donationValue) || 0;

                if (wasPayWithPoints !== isNowPayWithPoints) {
                    toast({
                        title: t("orderDetails.sections.costs.saveResult.paymentMethod.title"),
                        description: isNowPayWithPoints
                            ? t("orderDetails.sections.costs.payWithPointsEnabled")
                            : t("orderDetails.sections.costs.payWithPointsDisabled")
                    });
                } else if (editingField === "mileage" && oldMileage !== newMileage) {
                    toast({
                        title: t("orderDetails.sections.costs.saveResult.mileage.title"),
                        description: t("orderDetails.sections.costs.saveResult.mileage.description", {
                            oldValue: oldMileage,
                            newValue: newMileage
                        })
                    });
                } else if (editingField === "reimbursable" && oldReimbursable !== newReimbursable) {
                    toast({
                        title: t("orderDetails.sections.costs.saveResult.expenses.title"),
                        description: t("orderDetails.sections.costs.saveResult.expenses.description", {
                            oldValue: formatCurrency(oldReimbursable),
                            newValue: formatCurrency(newReimbursable)
                        })
                    });
                } else if (editingField === "donation" && oldDonation !== newDonation) {
                    toast({
                        title: t("orderDetails.sections.costs.saveResult.donation.title"),
                        description: t("orderDetails.sections.costs.saveResult.donation.description", {
                            oldValue: formatCurrency(oldDonation),
                            newValue: formatCurrency(newDonation)
                        })
                    });
                } else {
                    toast({
                        title: t("orderDetails.sections.costs.saveResult.success.title"),
                        description: t("orderDetails.sections.costs.saveResult.success.description")
                    });
                }
            }
        } catch (error: any) {
            log.error("Error saving costs:", error);

            // Revert local payment-method state on error
            setPayWithPoints(order.payWithPoints || false);
            setPaidFromSocialAccount(order.paidFromSocialAccount || false);

            if (error?.validationIssues?.length > 0) {
                const messages = error.validationIssues.map((v: any) => {
                    const key = v.message?.replace('orderForm.edit.errors.', '') || v.message;
                    return t(`orderForm.edit.errors.${key}`, { defaultValue: v.message });
                }).join(', ');
                toast({
                    title: t("orderDetails.sections.costs.saveResult.error.title"),
                    description: messages,
                    variant: "destructive"
                });
            } else {
                toast({
                    title: t("orderDetails.sections.costs.saveResult.error.title"),
                    description: error?.detail || t("orderDetails.sections.costs.saveResult.error.description"),
                    variant: "destructive"
                });
            }
        } finally {
            setIsLoading(false);
        }
    }

    function formatCurrency(value: number) {
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    }

    const isOrderCancelled = order.orderStatus === "Cancelled";
    const isOrderCompleted = order.orderStatus === "Completed";
    const hasHelper = order.helper !== null;
    const canEdit = !isOrderCancelled && !isOrderCompleted && hasHelper;

    return (
        <Card>
            <div className="p-6">
                <div className="relative mb-6">
                    <div className="text-2xl font-semibold leading-none tracking-tight" role="heading">
                        {t("orderDetails.sections.costs.heading")}
                    </div>
                </div>

                <div className="space-y-6">
                    {/* Info message when no helper is assigned */}
                    {!hasHelper && (
                        <div className="bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-400 dark:border-blue-500 p-4 flex items-start gap-3">
                            <Info
                                className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5"
                                aria-label={t("orderDetails.sections.costs.noHelperInfo")}
                            />
                            <Text className="text-blue-700 dark:text-blue-300">
                                {t("orderDetails.sections.costs.noHelperMessage")}
                            </Text>
                        </div>
                    )}

                    {/* Group: Helfer */}
                    <div>
                        <div className="border-b border-gray-200 dark:border-gray-700 pb-1 mb-3">
                            <Text className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                {t("orderDetails.sections.costs.helperGroup")}
                            </Text>
                        </div>

                        {order.pointsDonatedToSocialAccount && (
                            <div
                                className="mb-3 bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-400 dark:border-blue-500 p-3 flex items-start gap-2"
                                data-testid="points-donated-badge"
                            >
                                <PiggyBank className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                                <Text className="text-sm text-blue-700 dark:text-blue-300">
                                    {t("orderDetails.sections.costs.pointsDonatedToSocialAccount")}
                                </Text>
                            </div>
                        )}

                        {order.paidFromSocialAccount && isOrderCompleted && (
                            <div
                                className="mb-3 bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-400 dark:border-blue-500 p-3 flex items-start gap-2"
                                data-testid="paid-from-social-account-badge"
                            >
                                <PiggyBank className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                                <Text className="text-sm text-blue-700 dark:text-blue-300">
                                    {t("orderDetails.sections.costs.paidFromSocialAccount")}
                                </Text>
                            </div>
                        )}

                        <div className="space-y-2">
                            {/* Mileage */}
                            <Field>
                                <div className="flex justify-between items-center">
                                    <Label className="flex items-center gap-2">
                                        <Car className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                                        {t("orderDetails.sections.costs.mileage")}
                                    </Label>
                                    {editingField === "mileage" ? (
                                        <div className="flex gap-2" data-testid="mileage-edit">
                                            <Input
                                                type="number"
                                                value={mileageValue}
                                                onChange={(e) => setMileageValue(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleSave();
                                                }}
                                                min="0"
                                                className="w-24"
                                                data-testid="mileage-input"
                                            />
                                            <Button onClick={() => handleSave()} disabled={isLoading} data-testid="mileage-save">
                                                {t("orderDetails.sections.costs.save")}
                                            </Button>
                                            <Button
                                                plain
                                                onClick={() => {
                                                    setEditingField(null);
                                                    setMileageValue(order.mileage?.toString() || "0");
                                                }}
                                                data-testid="mileage-cancel"
                                            >
                                                {t("orderDetails.sections.costs.cancel")}
                                            </Button>
                                        </div>
                                    ) : (
                                        <div
                                            className={`flex items-center gap-2 ${canEdit ? "cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700" : ""} px-2 py-1 rounded`}
                                            onClick={() => canEdit && setEditingField("mileage")}
                                            data-testid="mileage-display"
                                        >
                                            <Text data-testid="mileage-value">
                                                {costs?.mileage || 0} km
                                                {costs && costs.mileage > 0 && ` (${formatCurrency(costs.mileageCost)})`}
                                            </Text>
                                            {canEdit && <Pencil className="h-3 w-3 text-gray-400" />}
                                        </div>
                                    )}
                                </div>
                            </Field>

                            {/* Reimbursable Amount */}
                            <Field>
                                <div className="flex justify-between items-center">
                                    <Label className="flex items-center gap-2">
                                        <Euro className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                                        {t("orderDetails.sections.costs.reimbursableAmount")}
                                    </Label>
                                    {editingField === "reimbursable" ? (
                                        <div className="flex gap-2" data-testid="reimbursable-edit">
                                            <Input
                                                type="number"
                                                value={reimbursableValue}
                                                onChange={(e) => setReimbursableValue(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleSave();
                                                }}
                                                min="0"
                                                step="0.01"
                                                className="w-24"
                                                data-testid="reimbursable-input"
                                            />
                                            <Button onClick={() => handleSave()} disabled={isLoading} data-testid="reimbursable-save">
                                                {t("orderDetails.sections.costs.save")}
                                            </Button>
                                            <Button
                                                plain
                                                onClick={() => {
                                                    setEditingField(null);
                                                    setReimbursableValue(order.reimbursableAmount?.toString() || "0.00");
                                                }}
                                                data-testid="reimbursable-cancel"
                                            >
                                                {t("orderDetails.sections.costs.cancel")}
                                            </Button>
                                        </div>
                                    ) : (
                                        <div
                                            className={`flex items-center gap-2 ${canEdit ? "cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700" : ""} px-2 py-1 rounded`}
                                            onClick={() => canEdit && setEditingField("reimbursable")}
                                            data-testid="reimbursable-display"
                                        >
                                            <Text data-testid="reimbursable-value">{formatCurrency(costs?.reimbursableAmount || 0)}</Text>
                                            {canEdit && <Pencil className="h-3 w-3 text-gray-400" />}
                                        </div>
                                    )}
                                </div>
                            </Field>
                        </div>
                    </div>

                    {/* Group: Verwaltung */}
                    <div>
                        <div className="border-b border-gray-200 dark:border-gray-700 pb-1 mb-3">
                            <Text className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                {t("orderDetails.sections.costs.adminGroup")}
                            </Text>
                        </div>

                        <div className="space-y-2">
                            {/* Activity Fee */}
                            <Field>
                                <div className="flex justify-between items-center">
                                    <Label className="flex items-center gap-2">
                                        <Building2 className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                                        {t("orderDetails.sections.costs.activityFees")}
                                    </Label>
                                    <Text className="font-medium px-2 py-1">
                                        {payWithPoints
                                            ? t("orderDetails.sections.costs.activityFeeInPoints", { points: costs?.activityPoints || 0 })
                                            : formatCurrency(costs?.activityFees || 0)}
                                    </Text>
                                </div>
                            </Field>

                            {/* Donation */}
                            <Field>
                                <div className="flex justify-between items-center">
                                    <Label className="flex items-center gap-2">
                                        <Heart className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                                        {t("orderDetails.sections.costs.donationAmount")}
                                    </Label>
                                    {editingField === "donation" ? (
                                        <div className="flex gap-2" data-testid="donation-edit">
                                            <Input
                                                type="number"
                                                value={donationValue}
                                                onChange={(e) => setDonationValue(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleSave();
                                                }}
                                                min="0"
                                                step="0.01"
                                                className="w-24"
                                                data-testid="donation-input"
                                            />
                                            <Button onClick={() => handleSave()} disabled={isLoading} data-testid="donation-save">
                                                {t("orderDetails.sections.costs.save")}
                                            </Button>
                                            <Button
                                                plain
                                                onClick={() => {
                                                    setEditingField(null);
                                                    setDonationValue(order.donationAmount?.toString() || "0.00");
                                                }}
                                                data-testid="donation-cancel"
                                            >
                                                {t("orderDetails.sections.costs.cancel")}
                                            </Button>
                                        </div>
                                    ) : (
                                        <div
                                            className={`flex items-center gap-2 ${canEdit ? "cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700" : ""} px-2 py-1 rounded`}
                                            onClick={() => canEdit && setEditingField("donation")}
                                            data-testid="donation-display"
                                        >
                                            <Text data-testid="donation-value">{formatCurrency(order.donationAmount || 0)}</Text>
                                            {canEdit && <Pencil className="h-3 w-3 text-gray-400" />}
                                        </div>
                                    )}
                                </div>
                            </Field>
                        </div>
                    </div>

                    {/* Group: Bezahlung */}
                    <div>
                        <div className="border-b border-gray-200 dark:border-gray-700 pb-1 mb-3">
                            <Text className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                {t("orderDetails.sections.costs.paymentGroup")}
                            </Text>
                        </div>

                        <div className="space-y-3 px-2">
                            <RadioGroup
                                value={paidFromSocialAccount ? "social" : (payWithPoints ? "points" : "invoice")}
                                onChange={(val: string) => {
                                    if (!canEdit) return;
                                    const newPayWithPoints = val === "points" || val === "social";
                                    const newPaidFromSocial = val === "social";
                                    setPayWithPoints(newPayWithPoints);
                                    setPaidFromSocialAccount(newPaidFromSocial);
                                    handleSave({
                                        payWithPoints: newPayWithPoints,
                                        paidFromSocialAccount: newPaidFromSocial
                                    });
                                }}
                            >
                                <RadioField>
                                    <Radio value="invoice" disabled={!canEdit} />
                                    <Label>{t("orderDetails.sections.costs.payByInvoice")}</Label>
                                </RadioField>
                                <RadioField disabled={!costs?.hasEnoughPoints || !canEdit}>
                                    <Radio value="points" disabled={!costs?.hasEnoughPoints || !canEdit} />
                                    <Label>{t("orderDetails.sections.costs.payByPoints")}</Label>
                                    {costs && !costs.hasEnoughPoints && canEdit && (
                                        <Description>
                                            <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                                                <AlertTriangle className="h-3.5 w-3.5" />
                                                {t("orderDetails.sections.costs.insufficientPointsInfo", {
                                                    required: costs.activityPoints,
                                                    available: costs.clientPoints
                                                })}
                                            </span>
                                        </Description>
                                    )}
                                </RadioField>
                                {socialAccountEnabled && (
                                    <RadioField disabled={!canEdit}>
                                        <Radio value="social" disabled={!canEdit} data-testid="pay-by-social-account-radio" />
                                        <Label>{t("orderDetails.sections.costs.payBySocialAccount")}</Label>
                                    </RadioField>
                                )}
                            </RadioGroup>
                        </div>
                    </div>
                </div>
            </div>
        </Card>
    );
}
